import {
    Box,
    Button,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Portal,
    Text,
    VStack,
} from '@chakra-ui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

export interface DuplicateProvider {
  id: string;
  displayName: string;
}

export interface DuplicateLastNamePopoverProps {
  lastName: string;
  otherDuplicates: DuplicateProvider[];
  onNavigate?: (id: string) => void;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  children: React.ReactElement;
}

export const DuplicateLastNamePopover: React.FC<DuplicateLastNamePopoverProps> = ({
  lastName,
  otherDuplicates,
  onNavigate,
  placement = 'top',
  children,
}) => {
  const navigate = useNavigate();

  const handleNavigate = (id: string) => {
    if (onNavigate) {
      onNavigate(id);
    } else {
      navigate(`/providers/${id}`);
    }
  };

  const tooltipTitle = 'Duplicate Last Name Detected';
  const tooltipBody = `Multiple providers share the last name '${lastName}'. Confirm by NPI/practice before scheduling.`;

  return (
    <Popover
      trigger="hover"
      placement={placement}
      closeOnBlur={true}
      returnFocusOnClose={false}
      openDelay={300}
      closeDelay={500}
      isLazy
    >
      <PopoverTrigger>
        {children}
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          bg="gray.800"
          borderColor="gray.700"
          maxW="18rem"
          _focus={{
            boxShadow: 'none',
          }}
        >
          <PopoverArrow bg="gray.800" />
          <PopoverBody p="0.75rem">
            <VStack align="start" spacing="0.75rem">
              {/* Header */}
              <Box>
                <Text
                  fontWeight="600"
                  fontSize="0.875rem"
                  color="white"
                  mb="0.25rem"
                >
                  {tooltipTitle}
                </Text>
                <Text
                  fontSize="0.75rem"
                  lineHeight="1.4"
                  color="gray.200"
                >
                  {tooltipBody}
                </Text>
              </Box>

              {/* Duplicate Providers List */}
              {otherDuplicates.length > 0 && (
                <Box w="100%">
                  <Text
                    fontSize="0.75rem"
                    fontWeight="600"
                    mb="0.375rem"
                    color="white"
                  >
                    Other providers with same last name:
                  </Text>

                  <VStack
                    align="stretch"
                    spacing="0.25rem"
                    maxH="240px"
                    overflowY="auto"
                    pr="0.25rem"
                    sx={{
                      '&::-webkit-scrollbar': {
                        width: '4px',
                      },
                      '&::-webkit-scrollbar-track': {
                        bg: 'gray.700',
                        borderRadius: '2px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        bg: 'gray.500',
                        borderRadius: '2px',
                        '&:hover': {
                          bg: 'gray.400',
                        },
                      },
                    }}
                  >
                    {otherDuplicates.map(({ id, displayName }) => (
                      <Button
                        key={id}
                        variant="ghost"
                        size="sm"
                        w="100%"
                        h="auto"
                        minH="2rem"
                        p="0.375rem 0.5rem"
                        justifyContent="flex-start"
                        textAlign="left"
                        bg="transparent"
                        color="blue.300"
                        fontSize="0.75rem"
                        fontWeight="500"
                        borderRadius="0.25rem"
                        _hover={{
                          color: 'blue.100',
                          bg: 'blue.900',
                          textDecoration: 'underline',
                        }}
                        _focus={{
                          outline: '2px solid',
                          outlineColor: 'blue.400',
                          outlineOffset: '2px',
                          bg: 'blue.900',
                        }}
                        _active={{
                          bg: 'blue.800',
                          transform: 'translateY(1px)',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNavigate(id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            handleNavigate(id);
                          }
                        }}
                        role="link"
                        aria-label={`Navigate to provider ${displayName}`}
                        title={displayName}
                      >
                        <Box display="flex" alignItems="center" gap="0.375rem">
                          <Box
                            fontSize="0.625rem"
                            color="blue.400"
                            fontWeight="600"
                          >
                            →
                          </Box>
                          <Box
                            fontWeight="500"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            flex="1"
                            minW="0"
                          >
                            {displayName}
                          </Box>
                        </Box>
                      </Button>
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default DuplicateLastNamePopover;
